import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CookieJs from "js-cookie";
import get from "lodash/get";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import employeeService from "../../services/employee";
import employeePerformanceService from "../../services/employeePerformance";
import queryDefault from "../../utils/queryDefault";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", md: 760 },
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  maxHeight: "85vh",
  overflowY: "auto",
};

const getYearOptions = () => {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) => thisYear - index);
};

const toNumber = (value: string | number) => Number(value || 0);
const getMonthLabel = (month: number) =>
  monthOptions.find((item) => item.value === month)?.label || "-";

export default function ListEmployeePerformance() {
  const user = JSON.parse(CookieJs.get("USER") || "{}");
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const notification = useNotification();
  const alert = useAlert();

  const [pageSize, setPageSize] = useState<number>(10);
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [options, setOptions] = useState<{
    month: number;
    year: number;
    employee_id?: number;
  }>({
    month: currentMonth,
    year: currentYear,
  });

  const filterForm = useForm({
    defaultValues: {
      month: currentMonth,
      year: currentYear,
      employee_id: "",
    },
  });

  const editForm = useForm({
    defaultValues: {
      employee_id: 0,
      employee_name: "",
      month: 0,
      month_name: "",
      year: 0,
      head_region: "",
      sales_achieve: 0,
      peer_crew: 0,
      head_region_score: 0,
      mystery_shopper: 0,
      monthly_assessment: 0,
      late_attendance: 0,
      late_report: 0,
      teguran: 0,
      complaint: 0,
    },
  });

  const listEmployeePerformance = useQuery(
    ["employee-performance-list", options],
    ({ queryKey }) => employeePerformanceService.getEmployeePerformance(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const listEmployees = useQuery(
    ["employee-options"],
    () => employeeService.getEmployee({ keyword: "" }),
    {
      ...queryDefault,
    }
  );

  const deleteEmployeePerformance = useMutation(
    "delete-employee-performance",
    employeePerformanceService.deleteEmployeePerformance,
    {
      onSuccess: () => {
        listEmployeePerformance.refetch();
        notification.onOpen({
          message: "Employee performance deleted successfully",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const getEmployeePerformanceDetail = useMutation(
    "detail-employee-performance",
    employeePerformanceService.getEmployeePerformanceById,
    {
      onSuccess: (response: any) => {
        const data = get(response, "data", {});
        setEditingId(Number(data.id));
        editForm.reset({
          employee_id: Number(data.employee_id || 0),
          employee_name: String(data.employee_name || "-"),
          month: Number(data.month || 0),
          month_name: getMonthLabel(Number(data.month || 0)),
          year: Number(data.year || 0),
          head_region: String(data.head_region || ""),
          sales_achieve: Number(data.sales_achieve || 0),
          peer_crew: Number(data.peer_crew || 0),
          head_region_score: Number(data.head_region_score || 0),
          mystery_shopper: Number(data.mystery_shopper || 0),
          monthly_assessment: Number(data.monthly_assessment || 0),
          late_attendance: Number(data.late_attendance || 0),
          late_report: Number(data.late_report || 0),
          teguran: Number(data.teguran || 0),
          complaint: Number(data.complaint || 0),
        });
        setOpenEdit(true);
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to get detail",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const updateEmployeePerformance = useMutation(
    "update-employee-performance",
    employeePerformanceService.updateEmployeePerformance,
    {
      onSuccess: () => {
        setOpenEdit(false);
        listEmployeePerformance.refetch();
        notification.onOpen({
          message: "Employee performance updated successfully",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const uploadBulkEmployeePerformance = useMutation(
    "upload-bulk-employee-performance",
    employeePerformanceService.uploadBulkEmployeePerformance,
    {
      onSuccess: () => {
        listEmployeePerformance.refetch();
        notification.onOpen({
          message: "Bulk employee performance uploaded successfully",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to upload bulk file",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onDelete = (id: number) => {
    alert.onClose();
    deleteEmployeePerformance.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this employee performance record?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onFilter = (values: any) => {
    setOptions({
      month: Number(values.month),
      year: Number(values.year),
      ...(values.employee_id ? { employee_id: Number(values.employee_id) } : {}),
    });
  };

  const onClickEdit = (id: number) => {
    getEmployeePerformanceDetail.mutate(id);
  };

  const onSubmitEdit = (values: any) => {
    if (!editingId) {
      return;
    }

    updateEmployeePerformance.mutate({
      id: editingId,
      data: {
        employee_id: toNumber(values.employee_id),
        month: toNumber(values.month),
        year: toNumber(values.year),
        head_region: values.head_region,
        sales_achieve: toNumber(values.sales_achieve),
        peer_crew: toNumber(values.peer_crew),
        head_region_score: toNumber(values.head_region_score),
        mystery_shopper: toNumber(values.mystery_shopper),
        monthly_assessment: toNumber(values.monthly_assessment),
        late_attendance: toNumber(values.late_attendance),
        late_report: toNumber(values.late_report),
        teguran: toNumber(values.teguran),
        complaint: toNumber(values.complaint),
        updated_by: user.username,
      },
    });
  };

  const handleExport = async () => {
    try {
      await employeePerformanceService.exportEmployeePerformance({
        month: options.month,
        year: options.year,
      });
    } catch (error: any) {
      notification.onOpen({
        message: error?.response?.data?.message || "Failed to export data",
        type: "error",
        position: "top",
      });
    }
  };

  const handleExportSummary = async () => {
    try {
      await employeePerformanceService.exportEmployeePerformanceSummary({
        month: options.month,
        year: options.year,
      });
    } catch (error: any) {
      notification.onOpen({
        message: error?.response?.data?.message || "Failed to export summary",
        type: "error",
        position: "top",
      });
    }
  };

  const handleUploadBulk = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }

    const formData = new FormData();
    formData.append("updated_by", user.username);
    formData.append("file", event.target.files[0]);
    uploadBulkEmployeePerformance.mutate(formData);
    event.target.value = "";
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Employee Performance</title>
      </Helmet>

      <Breadcrumb
        label="Employee Performance"
        breadcrumbs={[{ label: "Employee Performance", href: "/app/employee-performance" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[4, 5]}>
        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <FormLabel>Month</FormLabel>
              <Controller
                name="month"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {monthOptions.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <FormLabel>Year</FormLabel>
              <Controller
                name="year"
                control={filterForm.control}
                render={({ field }) => (
                  <Select {...field}>
                    {getYearOptions().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 280 }}>
              <FormLabel>Employee (Optional)</FormLabel>
              <Controller
                name="employee_id"
                control={filterForm.control}
                render={({ field }) => (
                  <Select
                    {...field}
                    displayEmpty
                    value={field.value === undefined ? "" : field.value}
                  >
                    <MenuItem value="">All Employees</MenuItem>
                    {get(listEmployees, "data.data", []).map((employee: any) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.name || employee.employee_name || employee.username}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>

            <Box sx={{ alignSelf: "end" }}>
              <Button variant="outlined" onClick={filterForm.handleSubmit(onFilter)}>
                Apply Filter
              </Button>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <Button
              variant="contained"
              component={RouterLink}
              to="/app/employee-performance/create"
            >
              Create Record
            </Button>
            <Button variant="contained" onClick={handleExport}>
              Export Employee Performance
            </Button>
            <Button variant="contained" onClick={handleExportSummary}>
              Export Employee Performance Summary
            </Button>
            <LoadingButton
              variant="contained"
              component="label"
              loading={uploadBulkEmployeePerformance.isLoading}
            >
              Upload Bulk Employee Performance
              <input type="file" hidden onChange={handleUploadBulk} />
            </LoadingButton>
          </Stack>
        </Box>

        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listEmployeePerformance.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listEmployeePerformance, "data.data", [])}
            rowCount={get(listEmployeePerformance, "data.data", []).length}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={[
              { field: "employee_name", headerName: "Employee", minWidth: 170, flex: 1 },
              {
                field: "month",
                headerName: "Month",
                minWidth: 120,
                renderCell: (params) => getMonthLabel(Number(params.value)),
              },
              { field: "year", headerName: "Year", minWidth: 100 },
              { field: "head_region", headerName: "Head Region", minWidth: 150, flex: 1 },
              { field: "sales_achieve", headerName: "Sales Achieve", minWidth: 130 },
              { field: "peer_crew", headerName: "Peer Crew", minWidth: 110 },
              { field: "head_region_score", headerName: "Head Region Score", minWidth: 160 },
              { field: "mystery_shopper", headerName: "Mystery Shopper", minWidth: 150 },
              { field: "monthly_assessment", headerName: "Monthly Assessment", minWidth: 170 },
              { field: "late_attendance", headerName: "Late Attendance", minWidth: 140 },
              { field: "late_report", headerName: "Late Report", minWidth: 120 },
              { field: "teguran", headerName: "Teguran", minWidth: 110 },
              { field: "complaint", headerName: "Complaint", minWidth: 110 },
              {
                field: "sales_achieve_point",
                headerName: "Sales Achieve Point",
                minWidth: 170,
              },
              { field: "peer_crew_point", headerName: "Peer Crew Point", minWidth: 150 },
              {
                field: "head_region_point",
                headerName: "Head Region Point",
                minWidth: 160,
              },
              {
                field: "mystery_shopper_point",
                headerName: "Mystery Shopper Point",
                minWidth: 170,
              },
              {
                field: "monthly_assessment_point",
                headerName: "Monthly Assessment Point",
                minWidth: 190,
              },
              {
                field: "indisclipinary_point",
                headerName: "Indisciplinary Point",
                minWidth: 170,
              },
              { field: "complaint_point", headerName: "Complaint Point", minWidth: 140 },
              { field: "final_score", headerName: "Final Score", minWidth: 120 },
              { field: "rank", headerName: "Rank", minWidth: 90 },
              { field: "last_updated_by", headerName: "Last Updated By", minWidth: 150 },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                minWidth: 180,
                renderCell: (params) => (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => onClickEdit(Number(params.row.id))}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onClickDelete(Number(params.row.id))}
                    >
                      Delete
                    </Button>
                  </Stack>
                ),
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>

      <Modal open={openEdit} onClose={() => setOpenEdit(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Edit Employee Performance
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Controller
              name="employee_name"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Employee" fullWidth size="small" disabled />
              )}
            />
            <Controller
              name="month_name"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Month" fullWidth size="small" disabled />
              )}
            />
            <Controller
              name="year"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Year" fullWidth size="small" disabled />
              )}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="head_region"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Head Region" fullWidth size="small" />
              )}
            />
            <Controller
              name="sales_achieve"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Sales Achieve" type="number" fullWidth size="small" />
              )}
            />
            <Controller
              name="peer_crew"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Peer Crew" type="number" fullWidth size="small" />
              )}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="head_region_score"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Head Region Score"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />
            <Controller
              name="mystery_shopper"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Mystery Shopper"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />
            <Controller
              name="monthly_assessment"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Monthly Assessment"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="late_attendance"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Late Attendance"
                  type="number"
                  fullWidth
                  size="small"
                />
              )}
            />
            <Controller
              name="late_report"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Late Report" type="number" fullWidth size="small" />
              )}
            />
            <Controller
              name="teguran"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Teguran" type="number" fullWidth size="small" />
              )}
            />
            <Controller
              name="complaint"
              control={editForm.control}
              render={({ field }) => (
                <TextField {...field} label="Complaint" type="number" fullWidth size="small" />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setOpenEdit(false)}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={editForm.handleSubmit(onSubmitEdit)}
              loading={updateEmployeePerformance.isLoading}
            >
              Save
            </LoadingButton>
          </Stack>
        </Box>
      </Modal>
    </MainLayout>
  );
}
